import { DomClassList } from '@bryntum/schedulerpro';
import { differenceInMilliseconds } from 'date-fns';

import { OrderEntity, TradeEntity } from '@/common/types/entities';
import { useBaseOrder } from '@/features/basePlan/composables';
import { BaseActualStatus } from '@/features/basePlan/types';
import { getBaseActualColor } from '@/features/basePlan/utils';
import { getCollisionCounterForEntity } from '@/features/collisions/collisionUtils';
import { Collision } from '@/features/collisions/types';
import { getDisplayedOrderEndDate } from '@/features/orders/orderUtils';
import { useProjectStore } from '@/features/projects/projectStore';
import { statusNotSet, StatusReport } from '@/helpers/orders/status';
import {
  determineColorBrightness,
  getHexFromCssVariable,
  hasHighLuminance,
} from '@/helpers/utils/colors';
import { NodeName } from '@/repositories/utils/cache';
import {
  BaseScheduleEventParser,
  schedulerClassConfig,
} from '@/services/store/schedule/parsers/base';
import { ScheduleModelParser } from '@/services/store/schedule/parsers/types';
import {
  SchedulerEvent,
  SchedulerEventAppearance,
  SchedulerEventColorType,
} from '@/services/store/schedule/types/types';

export class OrderEventParser
  extends BaseScheduleEventParser
  implements ScheduleModelParser<OrderEntity, [Map<string, TradeEntity>, Map<string, Collision>]>
{
  public entityToModel(
    order: OrderEntity,
    trades: Map<string, TradeEntity>,
    collisions: Map<string, Collision>,
  ): SchedulerEvent {
    const baseInput = this.parseSharedInput(order, 'startAt', 'finishAt', NodeName.ORDER);

    const isHourly = useProjectStore().currentProject?.hourlyPlanningEnabled;

    const endDate = getDisplayedOrderEndDate(order, { isHourly: !!isHourly });
    const duration =
      endDate !== baseInput.endDate
        ? differenceInMilliseconds(endDate, baseInput.startDate)
        : baseInput.duration;

    return {
      ...baseInput,
      endDate,
      duration,
      calendarId: order.calendar?.id ?? null,
      cls: new DomClassList([schedulerClassConfig[NodeName.ORDER]]),
      appearance: this.getAppearance(order, trades),
      percentDone: statusNotSet(order.status)
        ? 0
        : // Inverse the percentages in order to show them on the right hand side with white opacity
          100 - (order?.progress ?? 0),
      resourceId: order.wbsSection?.id ?? null,
      manuallyScheduled: true,
      collisionCounter: getCollisionCounterForEntity(order.id, collisions),
    };
  }

  public getAppearance(
    order: OrderEntity,
    trades: Map<string, TradeEntity>,
  ): Record<number, SchedulerEventAppearance> {
    return {
      [SchedulerEventColorType.TRADE]: this.getTradeViewAppearance(order, trades),
      [SchedulerEventColorType.STATUS]: this.getStatusViewAppearance(order),
      [SchedulerEventColorType.BASE_ACTUAL]: this.getBaseActualViewAppearance(order),
    };
  }

  public getTradeViewAppearance(
    order: OrderEntity,
    trades: Map<string, TradeEntity>,
  ): SchedulerEventAppearance {
    const classList: string[] = [];
    const color =
      trades.get(order.tenantTradeVariation?.id ?? '')?.color ??
      getHexFromCssVariable(`--color-cool-grey-300`);
    const brightness = determineColorBrightness(color);

    // add black text for light colors
    if (brightness === 'light') classList.push('black-event-text');
    // add black borders for very light colors
    if (hasHighLuminance(color)) classList.push('black-event-border');
    // additional styling for light colors

    return {
      cls: classList,
      eventColor: color,
    };
  }

  private getStatusViewAppearance(order: OrderEntity): SchedulerEventAppearance {
    const status = order.status;

    return {
      cls: [`order-status-report-${status ?? StatusReport.NOT_SET}-event`],
      eventColor: '',
    };
  }

  private getBaseActualViewAppearance(order: OrderEntity): SchedulerEventAppearance {
    const orderStatus = useBaseOrder(ref(order.id)).value?.status ?? BaseActualStatus.UNAVAILABLE;
    const stateColor = getBaseActualColor(orderStatus);

    return {
      cls: [],
      eventColor: getHexFromCssVariable(stateColor),
    };
  }
}
